// Pure derivation helpers for the Domain Intelligence drawer.
import type {
  DomainClassifyResult,
  DomainCoverage,
  DomainRuleFinding,
  DomainRuleValidation,
} from "../../api/contentRegistryApi";
import {
  EMPTY_PLACEHOLDER,
  NOT_COVERED_STATUS,
  UNCOVERED_DOMAIN_CODE,
} from "../../constants";
import { pctValue } from "../../utils";

export interface DomainIntelDerived {
  coverage: DomainCoverage[];
  sortedCoverage: DomainCoverage[];
  matchedCoverage: DomainCoverage[];
  primary: DomainCoverage | null;
  coveredCount: number;
  totalCount: number;
  totalMappings: number;
  reqsAnalyzed: number;
  reqsMapped: number;
  reqsUnmapped: number;
  mappedPct: number;
  multiDomainPct: number;
  matchesPerClause: number;
  domainCountLabel: number;
  multiDomainReqs: number;
  maxConfidence: number | null;
  overallPct: string;
  stopped: boolean;
  zeroRequirements: boolean;
  warnings: string[];
  backendNote: string;
  hasBanner: boolean;
  docRefKey: string;
}

export interface DomainValidationSummary {
  totalRules: number;
  passedRules: number;
  notFoundRules: number;
  findings: number;
  passPct: number;
}

export function toRuleValidationList(payload: unknown): DomainRuleValidation[] {
  const rows: unknown[] = Array.isArray(payload)
    ? (payload as unknown[])
    : Array.isArray((payload as { data?: unknown } | null)?.data)
      ? ((payload as { data: unknown[] }).data)
      : [];
  return rows.filter((row): row is DomainRuleValidation => {
    const candidate = row as Partial<DomainRuleValidation> | null;
    return (
      !!candidate &&
      typeof candidate.ruleName === "string" &&
      candidate.ruleName.trim().length > 0
    );
  });
}

export function ruleFindings(
  rule: DomainRuleValidation | null | undefined,
): DomainRuleFinding[] {
  return Array.isArray(rule?.findings) ? rule.findings : [];
}

export function deriveDomainValidationSummary(
  rules: DomainRuleValidation[],
): DomainValidationSummary {
  const passedRules = rules.filter((rule) => ruleFindings(rule).length > 0).length;
  const findings = rules.reduce(
    (total, rule) => total + ruleFindings(rule).length,
    0,
  );
  return {
    totalRules: rules.length,
    passedRules,
    notFoundRules: Math.max(0, rules.length - passedRules),
    findings,
    passPct: rules.length === 0 ? 0 : Math.round((passedRules / rules.length) * 100),
  };
}

export function sortDomainCoverage(rows: DomainCoverage[]): DomainCoverage[] {
  return [...rows].sort((a, b) => {
    const ar = a.policyRequirements ?? 0;
    const br = b.policyRequirements ?? 0;
    if (br !== ar) return br - ar;
    const ac = pctValue(a.confidence) ?? -1;
    const bc = pctValue(b.confidence) ?? -1;
    return bc - ac;
  });
}

/** Domain rows with at least one policy-requirement mapping or a non-null
 *  confidence — i.e. rows worth showing in the coverage table. */
export function filterMatchedCoverage(
  rows: DomainCoverage[],
): DomainCoverage[] {
  return rows.filter((r) => {
    if ((r.policyRequirements ?? 0) > 0) return true;
    return pctValue(r.confidence) !== null;
  });
}

export function selectPrimaryDomain(
  sorted: DomainCoverage[],
): DomainCoverage | null {
  for (const c of sorted) {
    if (String(c.domainCode || "").toUpperCase() === UNCOVERED_DOMAIN_CODE) {
      continue;
    }
    const hasReqs = (c.policyRequirements ?? 0) > 0;
    const hasConf = pctValue(c.confidence) !== null;
    if (hasReqs || hasConf) return c;
  }
  return null;
}

export function countCoveredDomains(rows: DomainCoverage[]): number {
  return rows.filter((d) => {
    const s = (d.coverageStatus || "").toUpperCase();
    return s !== NOT_COVERED_STATUS && s !== UNCOVERED_DOMAIN_CODE;
  }).length;
}

export function computeMaxConfidence(rows: DomainCoverage[]): number | null {
  let max = 0;
  let has = false;
  rows.forEach((d) => {
    const c = pctValue(d.confidence);
    if (c !== null) {
      has = true;
      if (c > max) max = c;
    }
  });
  return has ? max : null;
}

export function formatOverallPct(max: number | null): string {
  if (max === null) return EMPTY_PLACEHOLDER;
  return `${Math.round(max * 100)}%`;
}

export function formatSharePct(v: number | null | undefined): string {
  if (v === null || v === undefined) return EMPTY_PLACEHOLDER;
  return `${v.toFixed(1)}%`;
}

export function formatConfidenceScore(v: number | null | undefined): string {
  if (v === null || v === undefined) return EMPTY_PLACEHOLDER;
  return v.toFixed(2);
}

export function deriveDomainIntel(
  result: DomainClassifyResult | null,
  documentTitle?: string,
): DomainIntelDerived {
  const coverage = result?.coverage ?? [];
  const summary = result?.summary;
  const sortedCoverage = sortDomainCoverage(coverage);
  const matchedCoverage = filterMatchedCoverage(sortedCoverage);
  const primary = selectPrimaryDomain(sortedCoverage);
  const coveredCount = countCoveredDomains(coverage);
  const totalCount = coverage.length || result?.taxonomy?.domainCount || 0;
  const maxConfidence = computeMaxConfidence(coverage);
  const warnings = (result?.taxonomy?.warnings ?? []).filter(Boolean);
  const stopped = result?.stopped === true;
  const reqsAnalyzed = summary?.requirementsAnalyzed ?? 0;
  const reqsMapped = summary?.requirementsMapped ?? 0;
  const reqsUnmapped = summary?.requirementsUnmapped ?? Math.max(0, reqsAnalyzed - reqsMapped);
  const multiDomainReqs = summary?.multiDomainRequirements ?? 0;
  const totalMappings = summary?.totalDomainMappings ?? 0;
  const mappedPct = reqsAnalyzed ? Math.round((reqsMapped / reqsAnalyzed) * 100) : 0;
  const multiDomainPct = reqsMapped ? Math.round((multiDomainReqs / reqsMapped) * 100) : 0;
  const matchesPerClause = reqsMapped ? +(totalMappings / reqsMapped).toFixed(2) : 0;
  const zeroRequirements = reqsAnalyzed === 0;

  return {
    coverage,
    sortedCoverage,
    matchedCoverage,
    primary,
    coveredCount,
    totalCount,
    totalMappings,
    reqsAnalyzed,
    reqsMapped,
    reqsUnmapped,
    mappedPct,
    multiDomainPct,
    matchesPerClause,
    domainCountLabel: result?.taxonomy?.domainCount ?? coverage.length,
    multiDomainReqs,
    maxConfidence,
    overallPct: formatOverallPct(maxConfidence),
    stopped,
    zeroRequirements,
    warnings,
    backendNote: (result?.note ?? "").trim(),
    hasBanner: stopped || zeroRequirements || warnings.length > 0,
    docRefKey: result?.document?.ref ?? documentTitle ?? "",
  };
}

/** Normalise noisy clause IDs like "§2-baseline-governance¶3:83" to a
 *  compact preview (section + colon suffix): "§2¶3:83". */
export function shortClauseId(raw: string): string {
  const cleaned = raw.replace(/-[^§¶:]+/g, "");
  return cleaned.trim();
}

export interface ClausePreview {
  visible: string[];
  extra: number;
}

export function buildClausePreview(
  ids: string[] | undefined,
  max: number,
): ClausePreview {
  const list = ids ?? [];
  return {
    visible: list.slice(0, max).map(shortClauseId),
    extra: Math.max(0, list.length - max),
  };
}

export function coverageStatusKey(
  status: string | null | undefined,
): "FULLY_COVERED" | "PARTIALLY_COVERED" | "WEAKLY_COVERED" | "NOT_COVERED" {
  const key = String(status ?? "").toUpperCase();
  if (
    key === "FULLY_COVERED" ||
    key === "PARTIALLY_COVERED" ||
    key === "WEAKLY_COVERED" ||
    key === "NOT_COVERED"
  ) {
    return key;
  }
  return "NOT_COVERED";
}

export function isDocumentShareEmpty(row: DomainCoverage): boolean {
  const share = row.sharePct;
  const noReqs = (row.policyRequirements ?? 0) === 0;
  return share === null || share === undefined || (share === 0 && noReqs);
}
